import { createClient } from '@deepgram/sdk';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';

export const useDeepgram = (onTranscribed: (text: string) => void) => {
    const { locale } = useLocale();

    const { refetch: fetchToken } = api.platoUser.command.voiceRecording.getDeepgramTempToken.useQuery(
        undefined,
        {
            enabled: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    const { isLoading: isTranscribing, mutate: transcribe } = useMutation(
        async ({ blob, duration }: { blob: Blob; duration: number }) => {
            const { data } = await fetchToken();

            const accessToken = data?.accessToken;

            if (!accessToken) {
                throw new Error('Failed to receive voice recording token');
            }

            const _deepgram = createClient({
                accessToken,
            });

            const baseUrl = _deepgram.getRequestUrl('v1/listen');

            // Add query parameters for transcription settings
            const url = new URL(baseUrl);

            url.searchParams.set('language', locale);
            url.searchParams.set('model', 'nova-3');
            url.searchParams.set('punctuate', 'true');
            url.searchParams.set('smart_format', 'true');
            url.searchParams.set('numerals', 'true');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'deepgram/audio+video',
                },
                body: blob,
            });

            if (!response.ok) {
                throw new Error('Failed to transcribe audio');
            }

            const result = await response.json();

            const transcript = result.results?.channels?.[0]?.alternatives?.[0]?.transcript || '';

            if (transcript.length === 0 && duration > 3000) {
                console.error(
                    'Deepgram returned empty transcription for audio > 3 seconds',
                    URL.createObjectURL(blob),
                );
                captureException(new Error('Deepgram returned empty transcription for audio > 3 seconds'));
            }

            // Extract transcript from Deepgram response
            return result.results?.channels?.[0]?.alternatives?.[0]?.transcript || '';
        },
        {
            onSettled: (transcript) => {
                onTranscribed(transcript ?? '');
            },
        },
    );

    return {
        isTranscribing,
        transcribe,
    };
};
